import { Component } from '@theme/component'
import { debounce, onDocumentReady } from '@theme/utilities'
import { MegaMenuHoverEvent } from '@theme/events'

const SHORT_ACTIVATE_DELAY = 0
const LONG_ACTIVATE_DELAY = 250
const DEACTIVATE_DELAY = 350

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu']

  #abortController = new AbortController()

  connectedCallback() {
    super.connectedCallback()

    this.overflowMenu?.addEventListener(
      'pointerleave',
      () => this.#debouncedDeactivate(),
      {
        signal: this.#abortController.signal,
      }
    )

    onDocumentReady(this.#preloadImages)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.#abortController.abort()
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  }

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay
    return value ? parseInt(value, 10) : 0
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    )
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel()
    this.#shortDebouncedActivateHandler.cancel()
    this.#longDebouncedActivateHandler.cancel()

    if (this.#state.activeItem) {
      this.#shortDebouncedActivateHandler(event)
    } else {
      this.#longDebouncedActivateHandler(event)
    }
  }

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel()

    this.dispatchEvent(new MegaMenuHoverEvent())

    this.removeAttribute('data-animating')

    if (!(event.target instanceof Element)) return

    let item = findMenuItem(event.target)

    if (!item || item == this.#state.activeItem) return

    const listItem = event.target.closest('.menu-list__list-item')
    const isDefaultSlot = listItem?.slot !== 'overflow'

    this.dataset.overflowExpanded = (!isDefaultSlot).toString()

    const previouslyActiveItem = this.#state.activeItem

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false'
    }

    this.#state.activeItem = item
    this.ariaExpanded = 'true'
    item.ariaExpanded = 'true'

    let submenu = findSubmenu(item)
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu
    }

    const submenuHeight = submenu
      ? Math.max(
          submenu.scrollHeight,
          submenu.offsetHeight,
          overflowMenuHeight
        )
      : 0

    this.style.setProperty('--submenu-height', `${submenuHeight}px`)
  }

  #shortDebouncedActivateHandler = debounce(
    this.#activateHandler,
    SHORT_ACTIVATE_DELAY
  )
  #longDebouncedActivateHandler = debounce(
    this.#activateHandler,
    LONG_ACTIVATE_DELAY
  )

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#shortDebouncedActivateHandler.cancel()
    this.#longDebouncedActivateHandler.cancel()

    if (!(event.target instanceof Element)) return

    const relatedTarget = event.relatedTarget
    const listItem = event.target.closest('.menu-list__list-item')

    if (
      listItem &&
      relatedTarget instanceof Node &&
      listItem.contains(relatedTarget)
    ) {
      return
    }

    const item = findMenuItem(event.target)

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate()
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return
    if (this.overflowHovered) return

    this.style.setProperty('--submenu-height', '0px')
    this.dataset.overflowExpanded = 'false'

    this.#state.activeItem = null
    this.ariaExpanded = 'false'
    item.ariaExpanded = 'false'
    item.setAttribute('data-animating', '')

    setTimeout(() => {
      item.removeAttribute('data-animating')
    }, this.animationDelay)
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY)

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]')
    images?.forEach((image) => image.removeAttribute('loading'))
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu)
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null

  if (element.closest('[data-overflow-more]')) {
    const overflowItem = element
      .closest('overflow-list')
      ?.querySelector('[slot="overflow"]')

    return overflowItem ? findMenuItem(overflowItem) : null
  }

  const listItem = element.closest('.menu-list__list-item')
  const menuitem =
    element.closest('[ref="menuitem"]') ??
    listItem?.querySelector('[ref="menuitem"]') ??
    element.querySelector('[ref="menuitem"]')

  return menuitem instanceof HTMLElement ? menuitem : null
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const listItem = element?.closest('.menu-list__list-item')
  const submenu = listItem?.querySelector('[ref="submenu[]"]')
  return submenu instanceof HTMLElement ? submenu : null
}

class HorizontalMenu extends HTMLElement {
  constructor() {
    super()
  }

  connectedCallback() {
    window.addEventListener('load', this.scrollToActiveLink.bind(this))
  }

  scrollToActiveLink() {
    const scrollContainer = this
    const links = scrollContainer.querySelectorAll('.horizontal-menu__link')
    const activeLink = scrollContainer.querySelector(
      '.horizontal-menu__link--active'
    )

    if (scrollContainer && links.length && activeLink) {
      const containerRect = scrollContainer.getBoundingClientRect()
      const activeIndex = Array.from(links).indexOf(activeLink)
      const isFirst = activeIndex === 0
      const isLast = activeIndex === links.length - 1

      let scrollOffset

      if (isFirst) {
        scrollOffset = 0
      } else if (isLast) {
        scrollOffset = scrollContainer.scrollWidth - containerRect.width
      } else {
        const linkRect = activeLink.getBoundingClientRect()
        scrollOffset =
          activeLink.offsetLeft -
          scrollContainer.offsetLeft -
          containerRect.width / 2 +
          linkRect.width / 2
      }

      scrollContainer.scrollTo({
        left: Math.max(0, scrollOffset),
        behavior: 'instant',
      })
    }
  }
}

customElements.define('horizontal-menu', HorizontalMenu)
